import React, { useState } from "react";
import { useCampaigns } from "../../hooks/useCampaigns";
import CustomPagination from "../../common/Pagination";
import ConfirmationModal from "../../common/ConfirmationModal";
import { TableStatus } from "../../components/TableStatus";
import CampaignModal from "./CampaignModal";

export default function CampaignPage() {
    const itemsPerPage = 10;
    const [currentPage, setCurrentPage] = useState(1);

    const [searchTerm, setSearchTerm] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [campaignToDelete, setCampaignToDelete] = useState(null);

    const {
        campaigns,
        pagination,
        isLoading,
        isError,
        error,
        createCampaign,
        updateCampaign,
        deleteCampaign,
        isCreating,
        isUpdating,
        isDeleting,
    } = useCampaigns(currentPage, itemsPerPage, appliedSearch);

    const handleSearch = () => {
        setAppliedSearch(searchTerm);
        setCurrentPage(1);
    };

    const handleReset = () => {
        setSearchTerm("");
        setAppliedSearch("");
        setCurrentPage(1);
    };

    const handleOpenModal = (campaign = null) => {
        setEditingCampaign(campaign);
        setIsModalOpen(true);
    };

    const handleModalSubmit = async (formData) => {
        try {
            if (editingCampaign) {
                await updateCampaign({ id: editingCampaign._id, data: formData });
            } else {
                await createCampaign(formData);
            }
            setIsModalOpen(false);
        } catch (error) {
            console.error("Failed to save campaign", error);
        }
    };

    const handleDelete = async () => {
        if (!campaignToDelete) return;
        await deleteCampaign(campaignToDelete._id);
        setCampaignToDelete(null);
        if (campaigns.length === 1 && currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    return (
        <>
            <div className="card shadow-sm">
                <div className="card-header bg-light d-flex justify-content-between align-items-center p-3">
                    <h4 className="mb-0 text-primary-emphasis">Campaign Management</h4>
                    <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleOpenModal()}
                    >
                        <i className="fas fa-plus me-1"></i> Add Campaign
                    </button>
                </div>

                <div className="card-body border-bottom">
                    <div className="row g-2">
                        <div className="col-md-4">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search campaigns..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            />
                        </div>
                        <div className="col-md-2">
                            <button className="btn btn-primary w-100" onClick={handleSearch}>
                                Search
                            </button>
                        </div>
                        <div className="col-md-2">
                            <button className="btn btn-outline-dark w-100" onClick={handleReset}>
                                Reset
                            </button>
                        </div>
                    </div>
                </div>

                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>No</th>
                                    <th>Image</th>
                                    <th>Title</th>
                                    <th>Apply By</th>
                                    <th>Location</th>
                                    <th>Tags</th>
                                    <th>Link</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <TableStatus
                                    status={isLoading ? "loading" : isError ? "failed" : "succeeded"}
                                    error={error}
                                    dataLength={campaigns.length}
                                    colSpan={8}
                                    emptyText="No campaigns found."
                                />

                                {!isLoading &&
                                    campaigns.map((c, index) => (
                                        <tr key={c._id}>
                                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>

                                            <td>
                                                {c?.imageUrl ? (
                                                    <img
                                                        src={c.imageUrl}
                                                        alt="Campaign"
                                                        style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                                                    />
                                                ) : (
                                                    <div style={{ width: "40px", height: "40px", backgroundColor: "#e9ecef", borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        <i className="fas fa-image text-secondary"></i>
                                                    </div>
                                                )}
                                            </td>

                                            <td>
                                                <div className="fw-bold d-flex align-items-center">
                                                    {c?.title}
                                                    {c?.isPopular && (
                                                        <span className="badge bg-danger ms-2" style={{ fontSize: "0.7rem" }}>
                                                            <i className="fas fa-fire me-1"></i>Popular
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td>{c?.applyBy}</td>
                                            <td>{c?.location}</td>
                                            <td>
                                                {Array.isArray(c.typeTags) && c.typeTags.length > 0
                                                    ? c.typeTags.map((tag, i) => (
                                                        <span key={i} className="badge bg-info text-dark me-1">
                                                            {tag}
                                                        </span>
                                                    ))
                                                    : "-"}
                                            </td>
                                            <td>
                                                {c?.link && (
                                                    <a
                                                        href={c.link}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-decoration-none"
                                                    >
                                                        Visit
                                                    </a>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-2">
                                                    <button
                                                        className="btn btn-sm btn-outline-primary mr-2"
                                                        onClick={() => handleOpenModal(c)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => setCampaignToDelete(c)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {pagination?.totalPages > 1 && (
                    <div className="card-footer bg-white">
                        <CustomPagination
                            currentPage={Number(currentPage)}
                            totalPages={Number(pagination.totalPages)}
                            totalItems={Number(pagination.totalRecords)}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
            </div>

            <CampaignModal
                show={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleModalSubmit}
                initialData={editingCampaign}
                isLoading={isCreating || isUpdating}
            />

            <ConfirmationModal
                show={campaignToDelete !== null}
                onClose={() => setCampaignToDelete(null)}
                onConfirm={handleDelete}
                title="Confirm Deletion"
                confirmText="Delete"
                confirmButtonVariant="danger"
                isLoading={isDeleting}
            >
                Are you sure you want to delete <strong>{campaignToDelete?.title}</strong>?
            </ConfirmationModal>
        </>
    );
}